// utils/notificationText.js
// ─────────────────────────────────────────────────────────────────────────
// The single place that turns a notification object into display text.
// Previously this logic was duplicated (and had drifted slightly) between
// NotificationsPage.jsx and NotificationListener.jsx, and was hardcoded in
// Arabic in both places regardless of the app's language setting. Both
// call sites now import this and pass their own `t` from useLanguage().
// ─────────────────────────────────────────────────────────────────────────

export function getCurrencyResetNotificationText(notification, language = 'ar', currencies = {}) {
  const payload = { ...notification.payload, ...notification };
  if (String(notification.type || '').toUpperCase() !== 'WALLET'
      || payload.reset !== true) return null;
  const code = String(payload.currency || notification.relatedEntityId || '').toUpperCase();
  const currency = currencies[code] || {};
  const ar = language === 'ar';
  const name = ar ? (currency.nameAr || payload.currencyNameAr) : (currency.nameEn || payload.currencyNameEn);
  const amount = Number(payload.resetAmount);
  const removed = Number.isFinite(amount) ? amount.toLocaleString(language) : '';
  return ar
    ? `تم تصفير رصيد العملة. تم تصفير رصيد ${name || 'هذه العملة'} لدى جميع المستخدمين.${removed ? ` الرصيد المخصوم: ${removed}.` : ''}`
    : `Currency balance reset. The ${name || code || 'currency'} balance was reset for all users.${removed ? ` Amount removed: ${removed}.` : ''}`;
}

const teamEvent = (notification) => {
  const payload = notification.payload || {};
  const explicit = notification.teamEvent || payload.teamEvent;
  if (explicit) return String(explicit).toUpperCase();
  if (String(notification.relatedEntityType || '').toUpperCase() === 'TEAM_JOIN_REQUEST') return 'JOIN_REQUEST';
  if (typeof notification.approved === 'boolean' || typeof payload.approved === 'boolean') return (notification.approved ?? payload.approved) ? 'REQUEST_APPROVED' : 'REQUEST_REJECTED';
  if (notification.teamRole || notification.role || payload.role) return 'ROLE_CHANGED';
  const title = String(notification.title || '').toLowerCase();
  if (title.includes('new team request') || title.includes('طلب انضمام جديد')) return 'JOIN_REQUEST';
  if (title.includes('new team member') || title.includes('عضو جديد')) return 'MEMBER_JOINED';
  if (title.includes('request approved') || title.includes('قبول طلب الانضمام')) return 'REQUEST_APPROVED';
  if (title.includes('request rejected') || title.includes('رفض طلب الانضمام')) return 'REQUEST_REJECTED';
  if (title.includes('invitation') || title.includes('دعوة إلى فريق')) return 'INVITATION';
  if (title.includes('role changed') || title.includes('تغيير دورك')) return 'ROLE_CHANGED';
  if (title.includes('removed from team') || title.includes('إزالتك من الفريق')) return 'MEMBER_REMOVED';
  return 'TEAM';
};

const localizedTeamRole = (role, language) => {
  const value = String(role || '').toUpperCase();
  if (language !== 'ar') return value ? value.toLowerCase().replaceAll('_', ' ') : '';
  return { OWNER: 'مالك الفريق', ADMIN: 'مشرف الفريق', MEMBER: 'عضو', VICE_LEADER: 'نائب القائد', LEADER: 'قائد الفريق' }[value] || role || '';
};

export function getTeamNotificationText(notification, language = 'ar', fallbackActorName = '') {
  const payload = notification.payload || {};
  const event = teamEvent(notification);
  const actor = fallbackActorName || notification.fromUserName || notification.actor?.displayName || notification.actor?.userHandle || '';
  const team = notification.teamName || payload.teamName || (['REQUEST_APPROVED', 'REQUEST_REJECTED', 'INVITATION', 'MEMBER_REMOVED'].includes(event) ? notification.body : '') || '';
  const role = localizedTeamRole(notification.teamRole || notification.role || payload.role || (event === 'ROLE_CHANGED' ? notification.body : ''), language);
  if (language === 'ar') {
    switch (event) {
      case 'JOIN_REQUEST': return actor ? `${actor} أرسل طلباً للانضمام إلى فريقك` : 'طلب جديد للانضمام إلى فريقك';
      case 'MEMBER_JOINED': return actor ? `انضم ${actor} إلى فريقك` : 'انضم عضو جديد إلى فريقك';
      case 'REQUEST_APPROVED': return team ? `تم قبول طلب انضمامك إلى فريق ${team}` : 'تم قبول طلب انضمامك إلى الفريق';
      case 'REQUEST_REJECTED': return team ? `تم رفض طلب انضمامك إلى فريق ${team}` : 'تم رفض طلب انضمامك إلى الفريق';
      case 'INVITATION': return team ? `${actor || 'أحد المستخدمين'} دعاك للانضمام إلى فريق ${team}` : 'تلقيت دعوة للانضمام إلى فريق';
      case 'ROLE_CHANGED': return role ? `تم تغيير دورك في الفريق إلى ${role}` : 'تم تغيير دورك في الفريق';
      case 'MEMBER_REMOVED': return team ? `تمت إزالتك من فريق ${team}` : 'تمت إزالتك من الفريق';
      default: return [notification.title, notification.body].filter(Boolean).join(' ') || 'إشعار متعلق بالفريق';
    }
  }
  switch (event) {
    case 'JOIN_REQUEST': return actor ? `${actor} requested to join your team` : 'New request to join your team';
    case 'MEMBER_JOINED': return actor ? `${actor} joined your team` : 'A new member joined your team';
    case 'REQUEST_APPROVED': return team ? `Your request to join ${team} was approved` : 'Your team join request was approved';
    case 'REQUEST_REJECTED': return team ? `Your request to join ${team} was declined` : 'Your team join request was declined';
    case 'INVITATION': return team ? `${actor || 'Someone'} invited you to join ${team}` : 'You received a team invitation';
    case 'ROLE_CHANGED': return role ? `Your team role was changed to ${role}` : 'Your team role was changed';
    case 'MEMBER_REMOVED': return team ? `You were removed from ${team}` : 'You were removed from the team';
    default: return [notification.title, notification.body].filter(Boolean).join(' ') || 'Team notification';
  }
}

export function getNotificationText(notification, t, language = 'ar') {
  const name = notification.fromUserName || t('notifications_page_unknown_user');
  const type = String(notification.type || '').toLowerCase();

  switch (type) {
    case 'chat_message':
      return t('notifications_page_text_chat_message', { name, snippet: notification.messageSnippet || '' });
    case 'follow':
      return t('notifications_page_text_follow', { name });
    case 'reaction':
      if (notification.postId && !notification.commentId && !notification.replyId)
        return t('notifications_page_text_reaction_post', { name });
      if (notification.postId && notification.commentId && !notification.replyId)
        return t('notifications_page_text_reaction_comment', { name });
      if (notification.postId && notification.commentId && notification.replyId)
        return t('notifications_page_text_reaction_reply', { name });
      return t('notifications_page_text_reaction_generic', { name });
    case 'mention':
    case 'post':
      return t('notifications_page_text_mention', { name });
    case 'comment': {
      if (notification.coinsAmount && notification.coinsAmount > 0) {
        return t('notifications_page_text_comment_with_coins', { name, coins: notification.coinsAmount });
      }
      if (notification.message) {
        return t('notifications_page_text_comment_with_message', { name, message: notification.message });
      }
      return t('notifications_page_text_comment', { name });
    }
    case 'coins_with_comment':
      return notification.message
        ? t('notifications_page_text_comment_with_message', { name, message: notification.message })
        : t('notifications_page_text_coins_with_comment', { name, coins: notification.coinsAmount || '' });
    case 'reply': {
      if (notification.coinsAmount && notification.coinsAmount > 0) {
        return t('notifications_page_text_reply_with_coins', { name, coins: notification.coinsAmount });
      }
      if (notification.message) {
        return t('notifications_page_text_reply_with_message', { name, message: notification.message });
      }
      return t('notifications_page_text_reply', { name });
    }
    case 'private_message':
      return t('notifications_page_text_private_message', { name, snippet: notification.messageSnippet || '' });
    case 'group_message':
      return t('notifications_page_text_group_message', { name, group: notification.groupName || '', snippet: notification.messageSnippet || '' });
    case 'team_request':
      return notification.message || t('notifications_page_text_team_request');
    case 'team_approval':
      return notification.message || t('notifications_page_text_team_approval', { team: notification.teamName || '' });
    case 'team_promotion':
      return notification.message || t('notifications_page_text_team_promotion', {
        team: notification.teamName || '',
        role: notification.teamRole ? t('notifications_page_text_team_promotion_role', { role: notification.teamRole }) : '',
      });
    case 'team_kick':
      return notification.message || t('notifications_page_text_team_kick', { team: notification.teamName || '' });
    case 'team':
      return getTeamNotificationText(notification, language, name);
    case 'saraha':
      if (notification.message) {
        return notification.isAnonymous
          ? t('notifications_page_text_saraha_named', { message: notification.message })
          : t('notifications_page_text_saraha_from', { name, message: notification.message });
      }
      return notification.isAnonymous
        ? t('notifications_page_text_saraha_anonymous')
        : t('notifications_page_text_saraha_generic');
    case 'coins_sent':
      return notification.message || t('notifications_page_text_coins_sent', { name, coins: notification.coinsAmount || '' });
    case 'post_blocked':          return notification.message || t('notifications_page_post_blocked');
    case 'profile_image_blocked': return notification.message || t('notifications_page_profile_image_blocked');
    case 'cover_image_blocked':   return notification.message || t('notifications_page_cover_image_blocked');
    case 'blocked':
      return t(notification.originType === 'reply' ? 'notifications_page_reply_blocked' : 'notifications_page_comment_blocked', { reason: notification.reason });
    case 'unblocked':
      return t(notification.originType === 'reply' ? 'notifications_page_reply_unblocked' : 'notifications_page_comment_unblocked');
    default:
      return t('notifications_page_text_default');
  }
}
